import { useState, useEffect, useCallback } from 'react';
import { MobileOptimizer, ViewportManager, DeviceDetector } from '../utils/mobileOptimizer';

/**
 * React hook for mobile optimization utilities
 * Provides reactive mobile optimization state and utilities
 */
export const useMobileOptimization = () => {
  const [optimizationState, setOptimizationState] = useState(() => {
    // Safety check for test environment
    if (typeof window === 'undefined' || !MobileOptimizer?.getOptimizationState) {
      return {
        device: { type: 'desktop', isMobile: false, isTablet: false, isDesktop: true, isTouchDevice: false, orientation: 'landscape' },
        viewport: { width: 1024, height: 768 },
        breakpoints: { current: 'desktop', isMobile: false, isTablet: false, isDesktop: true, isLargeDesktop: false, width: 1024, height: 768 },
        touchOptimizations: {}
      };
    }
    return MobileOptimizer.getOptimizationState();
  });

  // Update state when viewport changes
  useEffect(() => {
    // Safety check for test environment
    if (typeof window === 'undefined' || !MobileOptimizer?.getOptimizationState) {
      return;
    }
    
    const updateState = () => {
      if (MobileOptimizer?.getOptimizationState) {
        setOptimizationState(MobileOptimizer.getOptimizationState());
      }
    };

    const cleanupResize = ViewportManager?.addResizeListener ? ViewportManager.addResizeListener(updateState) : () => {};
    const cleanupOrientation = ViewportManager?.addOrientationListener ? ViewportManager.addOrientationListener(updateState) : () => {};

    return () => {
      cleanupResize();
      cleanupOrientation();
    };
  }, []);

  // Utility functions
  const isMobile = useCallback(() => optimizationState.device.isMobile, [optimizationState]);
  const isTablet = useCallback(() => optimizationState.device.isTablet, [optimizationState]);
  const isDesktop = useCallback(() => optimizationState.device.isDesktop, [optimizationState]);
  const isTouchDevice = useCallback(() => optimizationState.device.isTouchDevice, [optimizationState]);
  const getOrientation = useCallback(() => optimizationState.device.orientation, [optimizationState]);
  const getDeviceType = useCallback(() => optimizationState.device.type, [optimizationState]);

  return {
    // State
    ...optimizationState,
    
    // Utility functions
    isMobile,
    isTablet,
    isDesktop,
    isTouchDevice,
    getOrientation,
    getDeviceType,
    
    // Direct access to utility classes
    DeviceDetector,
    ViewportManager,
    MobileOptimizer
  };
};

/**
 * Hook for responsive breakpoint detection
 */
export const useBreakpoint = () => {
  const { breakpoints } = useMobileOptimization();
  
  return {
    isMobile: breakpoints.isMobile,
    isTablet: breakpoints.isTablet,
    isDesktop: breakpoints.isDesktop,
    isLargeDesktop: breakpoints.isLargeDesktop,
    current: breakpoints.current,
    width: breakpoints.width,
    height: breakpoints.height
  };
};

/**
 * Hook for orientation detection
 */
export const useOrientation = () => {
  const [orientation, setOrientation] = useState(() => {
    // Safety check for test environment
    if (typeof window === 'undefined' || !DeviceDetector?.getOrientation) {
      return 'landscape';
    }
    return DeviceDetector.getOrientation();
  });

  useEffect(() => {
    // Safety check for test environment
    if (typeof window === 'undefined' || !ViewportManager?.addOrientationListener) {
      return;
    }
    
    const cleanup = ViewportManager.addOrientationListener(({ orientation: newOrientation }) => {
      setOrientation(newOrientation);
    });

    return cleanup;
  }, []);

  return {
    orientation,
    isPortrait: orientation === 'portrait',
    isLandscape: orientation === 'landscape'
  };
};